import * as React from 'react'
import { StackChildren, Icon, Text } from '@doordash/design-language'
import NavBar from '@doordash/component-navbar'
import Button from '@doordash/component-button'
import { PhoneField } from '@doordash/component-fields'
import {
  RiskActions,
  RiskContext,
  RiskWorkflows,
} from '@doordash/component-risk'
import { Mode, Channel } from './types'
import { connect } from 'react-redux'
import * as actions from '../../../actions'
import { useToast } from '@doordash/component-toast'
import * as events from '../../../events'
import * as DA from '../../../typeModules/events'
import t from './strings'

type ReduxProps = {
  country?: string
  currentPhoneNumber?: string
  updatePhoneNumber: (formattedPhoneNumber: string) => Promise<void>
  trackWebviewMultiFactorAuth: (data: DA.WebviewMultiFactorAuth) => Promise<any>
}

type Props = {
  setMode: React.Dispatch<React.SetStateAction<Mode>>
  setChannel: React.Dispatch<React.SetStateAction<Channel>>
  setVerified: React.Dispatch<React.SetStateAction<boolean>>
}

export const UpdatePhoneNumber: React.FC<Props & ReduxProps> = ({
  setMode,
  setChannel,
  country = 'US',
  updatePhoneNumber,
  currentPhoneNumber,
  trackWebviewMultiFactorAuth,
}) => {
  const [phoneNumber, setPhoneNumber] = React.useState('')
  const [countryCode, setCountryCode] = React.useState(country)
  const { dispatch } = React.useContext(RiskContext)
  const { displayToast } = useToast()

  React.useEffect(() => {
    trackWebviewMultiFactorAuth({
      action: DA.EventActions.Rendered,
      name: DA.WebviewMultiFactorAuthNames.UpdatePhone,
    })
  }, [])

  const phoneNumberLastFour = currentPhoneNumber.slice(
    currentPhoneNumber.length - 4
  )

  const savePhoneNumber = () => {
    trackWebviewMultiFactorAuth({
      action: DA.EventActions.Clicked,
      name: DA.WebviewMultiFactorAuthNames.UpdatePhone,
    })

    dispatch({
      type: RiskActions.RegisterCallback,
      onClose: () => {
        trackWebviewMultiFactorAuth({
          action: DA.EventActions.Close,
          name: DA.WebviewMultiFactorAuthNames.UpdatePhone,
        })

        setChannel(Channel.Sms)
      },
      onSuccess: async () => {
        try {
          await updatePhoneNumber(phoneNumber)

          trackWebviewMultiFactorAuth({
            action: DA.EventActions.Success,
            name: DA.WebviewMultiFactorAuthNames.UpdatePhone,
          })

          displayToast({
            icon: Icon.Types.Check,
            text: t.updatePhone.successNotification(),
          })
          setMode(Mode.Default)
        } catch(e) {
          trackWebviewMultiFactorAuth({
            action: DA.EventActions.Error,
            name: DA.WebviewMultiFactorAuthNames.UpdatePhone,
          })
        }
      },
    })

    setChannel(Channel.Email)
    dispatch({
      type: RiskActions.VerificationRequired,
      workflow: RiskWorkflows.DasherLogin,
    })
  }

  return (
    <React.Fragment>
      <NavBar
        navigationType={NavBar.NavigationTypes.Back}
        isSeparatorVisible={false}
        onNavigationClick={() => {
          trackWebviewMultiFactorAuth({
            action: DA.EventActions.Back,
            name: DA.WebviewMultiFactorAuthNames.UpdatePhone,
          })

          setMode(Mode.Default)
        }}
      />
      <StackChildren>
        <Text
          styles={Text.Styles.MajorPageTitle}
          textAlign={Text.TextAlignments.Center}
        >
          {t.updatePhone.title()}
        </Text>
        <Text
          styles={Text.Styles.PageSubtext}
          textAlign={Text.TextAlignments.Center}
        >
          {t.updatePhone.subtext1()}
        </Text>
        <Text
          styles={Text.Styles.PageSubtext}
          textAlign={Text.TextAlignments.Center}
        >
          {t.updatePhone.subtext2()}
        </Text>
        <Text
          styles={Text.Styles.PageSubtext}
          textAlign={Text.TextAlignments.Center}
        >
          {t.updatePhone.subtext3(phoneNumberLastFour)}
        </Text>
        <PhoneField
          type="tel"
          label={t.updatePhone.phoneFieldLabel()}
          currentCountry={countryCode}
          isInternationalEnabled
          value={phoneNumber}
          onChange={(_, formatted) => {
            setPhoneNumber(formatted.number.number)
          }}
          onCountryChange={(countrySelected, _, formatted) => {
            setCountryCode(countrySelected)
            setPhoneNumber(formatted.number.number)
          }}
        />
        <Button onClick={savePhoneNumber}>
          {t.updatePhone.primaryAction()}
        </Button>
      </StackChildren>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  currentPhoneNumber: state.application.phoneNumber,
  country: state.application.country,
})

const mapDispatchToProps = {
  updatePhoneNumber: actions.updatePhoneNumber,
  trackWebviewMultiFactorAuth: events.trackWebviewMultiFactorAuth,
}

export default connect(mapStateToProps, mapDispatchToProps)(UpdatePhoneNumber)
